import apiMonitor from '../utils/apiMonitor';

/**
 * Centralized API Cache Service
 * Prevents duplicate API calls and implements intelligent caching
 */

class ApiCacheService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.cacheTimestamps = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
  }

  /**
   * Generate cache key from URL and params
   */
  generateCacheKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${url}?${JSON.stringify(sortedParams)}`;
  }

  /**
   * Check if cache entry is still valid
   */
  isCacheValid(key, ttl = this.defaultTTL) {
    const timestamp = this.cacheTimestamps.get(key);
    if (!timestamp) return false;
    
    return (Date.now() - timestamp) < ttl;
  }

  /**
   * Get cached data if valid
   */
  getCached(key) {
    if (this.cache.has(key) && this.isCacheValid(key)) {
      console.log(`🎯 Cache hit for: ${key}`);
      return this.cache.get(key);
    }
    
    if (this.cache.has(key)) {
      console.log(`⏰ Cache expired for: ${key}`);
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
    }
    
    return null;
  }

  /**
   * Set cached data with timestamp
   */
  setCached(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, data);
    this.cacheTimestamps.set(key, Date.now());
    console.log(`💾 Cached data for: ${key} (TTL: ${ttl}ms)`);
  }

  /**
   * Check if request is already pending
   */
  isRequestPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Add pending request
   */
  addPendingRequest(key, promise) {
    this.pendingRequests.set(key, promise);
  }

  /**
   * Remove pending request
   */
  removePendingRequest(key) {
    this.pendingRequests.delete(key);
  }

  /**
   * Get pending request
   */
  getPendingRequest(key) {
    return this.pendingRequests.get(key);
  }

  /**
   * Clear all cache
   */
  clearCache() {
    this.cache.clear();
    this.cacheTimestamps.clear();
    this.pendingRequests.clear();
    console.log('🧹 Cache cleared');
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(key) {
    this.cache.delete(key);
    this.cacheTimestamps.delete(key);
    this.pendingRequests.delete(key);
    console.log(`🗑️ Cleared cache entry: ${key}`);
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      cacheKeys: Array.from(this.cache.keys()),
      timestamps: Object.fromEntries(this.cacheTimestamps)
    };
  }
}

// Create singleton instance
const apiCacheService = new ApiCacheService();

export default apiCacheService;
